import { useState } from 'react';

import { Person } from '@api/lendingAPI/CustomerStateAPIHelper';
import { logError } from '@iwLogger/IwDataDogLogger';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    PutStateRequestBody,
    fetchGetStateByStateKey,
    fetchPutState
} from '@iwoca/lapi-client/edge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useStateKey } from '../../../../../../iwQueryClient/useStateKey';
import {
    convertDeserialisedPersonToFormValues,
    createCustomerStateWithNewOrUpdatedPerson
} from '../../../../../funding/fundingSteps/majorShareholderCheckAction/mshModal/MSHForm.helpers';
import { TFormValues } from '../../../../../funding/fundingSteps/majorShareholderCheckAction/mshModal/MSHForm.types';

export function useConfirmSavedShareholder() {
    const stateKey = useStateKey();
    const queryClient = useQueryClient();
    const { data: customerState } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey })
    });
    const [confirmingPersonUid, setConfirmingPersonUid] = useState<
        string | undefined
    >();
    const [hasError, setHasError] = useState(false);

    const saveCustomerState = useMutation({
        mutationFn: (state: PutStateRequestBody) =>
            fetchPutState({ stateKey, body: state }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
            await queryClient.invalidateQueries({
                queryKey: ['fetchGetFundingRequirementsCreditFacility']
            });
        }
    });

    const confirmShareholder = async (person: Person) => {
        if (!customerState) return;
        const values = {
            ...convertDeserialisedPersonToFormValues(person),
            shareholder: true
        } as TFormValues;
        const newState = createCustomerStateWithNewOrUpdatedPerson(
            values,
            customerState
        );
        setConfirmingPersonUid(person.uid);
        setHasError(false);
        try {
            await saveCustomerState.mutateAsync(newState);
            tracking.ampli.fundingRequirementsShareholderEdited({
                type: 'confirmed',
                version: '2.0'
            });
        } catch (error: unknown) {
            setHasError(true);
            logError(
                'Failed to confirm saved person as shareholder',
                {
                    tags: {
                        reporter:
                            'funding_requirements_confirm_shareholder_hook'
                    }
                },
                error as Error
            );
        } finally {
            setConfirmingPersonUid(undefined);
        }
    };

    return { confirmShareholder, confirmingPersonUid, hasError };
}
